<template>
    <b-modal
        :id="modalId"
        v-model="active"
        centered
        hideFooter
        headerClass="header"
        @show="setAttributesDefaultValues"
    >
        <template #modal-title>
            <h5 class="text-white">
                {{ $t("modules.materialDonations.confirmDonation") }}
            </h5>
        </template>
        <div>
            <div class="ml-2 mr-2 mt-1">
                <formulateForm
                    v-slot="{ hasErrors }"
                    v-model="formData"
                    name="materialDonationConfirmationForm"
                >
                    <b-row class="mb-2">
                        <b-col cols="12" md="3">
                            <b-avatar
                                variant="info"
                                :text="getNameInitials"
                                size="5rem"
                            ></b-avatar>
                        </b-col>

                        <b-col cols="12" md="9" class="mt-2">
                            <b-row>
                                <h4
                                    v-if="materialDonation.contact"
                                    class="text-truncate d-block"
                                >
                                    {{ materialDonation.contact.name }}
                                </h4>
                            </b-row>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col cols="12">
                            <formulateInput
                                v-if="materialDonationStatusOptions"
                                :label="$t('common.status')"
                                name="materialDonationStatus"
                                :placeholder="$t('common.status')"
                                type="select"
                                validation="required|max:32,length"
                                :options="filterStatus"
                            />
                        </b-col>

                        <b-col v-if="disableDate" cols="6">
                            <formulateInput
                                v-model="formData.withdrawalDate"
                                :label="
                                    $t(
                                        'modules.materialDonations.withdrawalDate'
                                    )
                                "
                                :placeholder="
                                    $t(
                                        'modules.materialDonations.withdrawalDate'
                                    )
                                "
                                name="withdrawalDate"
                                type="custom-date"
                                validation="required|date:YYYY-MM-DD"
                                @update:date="formData.withdrawalDate = $event"
                            >
                            </formulateInput>
                        </b-col>

                        <b-col v-if="disableDate" cols="6">
                            <formulateInput
                                v-model="formData.withdrawalTime"
                                :label="$t('modules.materialDonations.time')"
                                placeholder="HH:MM"
                                maxlength="5"
                                name="withdrawalTime"
                                type="text"
                                :validation="[
                                    [
                                        'matches',
                                        '/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/',
                                    ],
                                    ['required'],
                                ]"
                                @input="timeMask"
                            >
                            </formulateInput>
                        </b-col>

                        <b-col cols="12">
                            <formulateInput
                                v-if="!disableDate"
                                v-model="formData.materialDonationStatusReason"
                                :label="
                                    $t(
                                        'modules.materialDonations.materialDonationStatusReason'
                                    )
                                "
                                :placeholder="
                                    $t(
                                        'modules.materialDonations.materialDonationStatusReason'
                                    )
                                "
                                name="materialDonationStatusReason"
                                type="textarea"
                                maxlength="255"
                                rows="7"
                                validation="required|max:255,length"
                            >
                            </formulateInput>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col class="d-flex justify-content-center">
                            <formulateErrors />
                        </b-col>
                    </b-row>

                    <b-row class="mt-2">
                        <b-col class="d-flex justify-content-center">
                            <formulateInput
                                :disabled="hasErrors || isLoading"
                                :label="
                                    isLoading
                                        ? $t('common.crud.updating')
                                        : $t('common.crud.update')
                                "
                                inputClass="btn btn-primary"
                                type="submit"
                                @click="handleSubmit"
                            />
                        </b-col>

                        <b-col class="d-flex justify-content-center">
                            <FormulateInput
                                :label="$t('common.crud.close')"
                                inputClass="btn btn-danger"
                                type="button"
                                @click="hide"
                            />
                        </b-col>
                    </b-row>
                </formulateForm>
            </div>
        </div>
    </b-modal>
</template>

<script>
import { BRow, BCol, BModal, BAvatar } from "bootstrap-vue"
import Api from "@/services/api/index"
import toastMessages from "@/views/components/utilis-spa/utilities/ToastMessages"
import _ from "lodash"

export default {
    name: "MaterialDonationStatusConfirmationModal",
    components: {
        BAvatar,
        BRow,
        BCol,
        BModal,
    },
    props: {
        materialDonation: {
            type: Object,
            required: true,
        },
        materialDonationStatusOptions: {
            type: Array,
            required: true,
        },
        modalId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            active: false,
            formData: {
                materialDonationStatus: "",
                withdrawalDate: "",
                withdrawalTime: "",
            },
            isLoading: false,
        }
    },
    computed: {
        disableDate() {
            const donationStatus = this.materialDonationStatusOptions.find(
                el => el.id === this.formData.materialDonationStatus
            )

            if (!donationStatus) return true

            return ["confirmed"].includes(donationStatus.systemDefaultStatus)
        },
        withdrawalTime() {
            return this.formData.withdrawalTime
        },
        getNameInitials() {
            if (!_.get(this.materialDonation, "contact")) return ""

            const split = this.materialDonation.contact.name.split(" ", 2)
            if (split.length < 2) {
                return (
                    this.materialDonation.contact.name[0] +
                    this.materialDonation.contact.name[1]
                )
            }
            const first = split[0]
            const second = split[1]
            return first.charAt(0) + second.charAt(0)
        },
        filterStatus() {
            let filteredStatuses = []

            const filterStatus = ["cancelled", "rejected", "confirmed"]

            filteredStatuses = this.materialDonationStatusOptions
                .filter(el => filterStatus.includes(el.systemDefaultStatus))
                .map(el => ({
                    value: el.id,
                    label: el.description,
                }))

            return filteredStatuses
        },
    },
    watch: {
        withdrawalTime(newVal) {
            if (!newVal) return
            const numericValue = newVal.replace(/\D/g, "")
            if (numericValue.length <= 2) {
                this.formData.withdrawalTime = numericValue
            } else {
                this.formData.withdrawalTime = `${numericValue.slice(
                    0,
                    2
                )}:${numericValue.slice(2)}`
            }
        },
    },
    created() {
        this.isLoading = false
    },
    methods: {
        timeMask(event) {
            if (!event) return

            const inputValue = event.replace(/\D/g, "")

            if (inputValue.length <= 2) {
                this.formData.withdrawalTime = inputValue
            } else {
                this.formData.withdrawalTime = `${inputValue.slice(
                    0,
                    2
                )}:${inputValue.slice(2)}`
            }
        },
        show() {
            this.active = true
        },
        hide() {
            this.active = false
            this.setAttributesDefaultValues()
        },

        setAttributesDefaultValues() {
            this.formData.materialDonationStatus = ""
            this.formData.withdrawalDate = ""
            this.formData.withdrawalTime = ""
        },

        async handleSubmit() {
            try {
                this.isLoading = true
                const withdrawalDateTime = `${this.formData.withdrawalDate} ${this.formData.withdrawalTime}`
                const payload = {
                    data: {
                        type: "material-donations",
                        id: String(this.materialDonation.id),
                        attributes: {
                            materialDonationStatusReason: this.formData
                                .materialDonationStatusReason,
                        },
                    },
                }
                let response

                const status = this.materialDonationStatusOptions.filter(
                    el => el.id === this.formData.materialDonationStatus
                )
                if (status[0].systemDefaultStatus === "confirmed")
                    response = await Api.axios.patch(
                        `/api/v1/material-donations/${this.materialDonation.id}/-actions/confirm`,
                        {
                            data: {
                                type: "material-donations",
                                id: String(this.materialDonation.id),
                                attributes: {
                                    withdrawalDate: withdrawalDateTime,
                                    materialDonationStatusReason: this.formData
                                        .materialDonationStatusReason,
                                },
                            },
                        }
                    )
                else if (status[0].systemDefaultStatus === "cancelled")
                    response = await Api.axios.patch(
                        `/api/v1/material-donations/${this.materialDonation.id}/-actions/cancel`,
                        payload
                    )
                else if (status[0].systemDefaultStatus === "rejected")
                    response = await Api.axios.patch(
                        `/api/v1/material-donations/${this.materialDonation.id}/-actions/reject`,
                        payload
                    )

                this.$toast(toastMessages.getSuccessMessage())
                this.hide()
                this.$emit("updateMaterialDonationStatus", response)
            } catch (e) {
                this.$formulate.handle(e, "materialDonationConfirmationForm")
                this.createdCompany = null
                this.$toast(toastMessages.getUnexpectedError())
                this.$bvModal.hide(this.modalId)
            } finally {
                this.isLoading = false
            }
        },
    },
}
</script>

<style lang="scss">
.header {
    background-color: #519bd6 !important;
    width: 100%;
    color: #ffffff;
    position: relative;
}
.text-white {
    color: #ffffff;
}
</style>
